import type { ReactElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { toast } from 'sonner';

import { campaignAnalysisReportPdfStyles } from './campaign-analysis-report-pdf-styles';

function waitForPrintAssetReadiness(printWindow: Window) {
  const printDocument = printWindow.document;
  const stylesheetPromises = Array.from(
    printDocument.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  ).map((link) => {
    if (link.sheet) return Promise.resolve();

    return new Promise<void>((resolve) => {
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
    });
  });
  const fontPromise = printDocument.fonts?.ready.then(() => undefined).catch(() => undefined);
  const imagePromises = Array.from(printDocument.images).map((image) => {
    if (image.complete) return image.decode?.().catch(() => undefined) ?? Promise.resolve();

    return new Promise<void>((resolve) => {
      image.addEventListener(
        'load',
        () => {
          void (image.decode?.().catch(() => undefined) ?? Promise.resolve()).then(() => resolve());
        },
        { once: true },
      );
      image.addEventListener('error', () => resolve(), { once: true });
    });
  });

  return Promise.race([
    Promise.all([...stylesheetPromises, fontPromise, ...imagePromises]).then(() => undefined),
    new Promise<void>((resolve) => {
      printWindow.setTimeout(resolve, 3000);
    }),
  ]).then(
    () =>
      new Promise<void>((resolve) => {
        printWindow.requestAnimationFrame(() => {
          printWindow.requestAnimationFrame(() => {
            printWindow.setTimeout(resolve, 100);
          });
        });
      }),
  );
}

export async function exportCampaignAnalysisReportPdf({
  reportElement,
  reportTitle,
}: {
  reportElement: ReactElement;
  reportTitle: string;
}) {
  const printWindow = window.open('', '_blank', 'width=1280,height=1200');
  if (!printWindow) {
    toast.error('Allow pop-ups to export this report.');
    return;
  }

  try {
    const reportMarkup = renderToStaticMarkup(reportElement);
    const documentStyles = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style'),
      (node) => node.outerHTML,
    ).join('\n');

    printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <base href="${document.baseURI}" />
    ${documentStyles}
    <style>${campaignAnalysisReportPdfStyles}</style>
  </head>
  <body>
    <div id="campaign-analysis-report-pdf-root">${reportMarkup}</div>
  </body>
</html>`);
    printWindow.document.close();
    Array.from(printWindow.document.images).forEach((image) => {
      image.loading = 'eager';
    });
    printWindow.document.title = reportTitle;

    await waitForPrintAssetReadiness(printWindow);
    printWindow.focus();
    printWindow.print();
    toast.success('Use the print dialog to save this report as a PDF.');
  } catch {
    printWindow.close();
    toast.error('Report export failed. Please try again.');
  }
}
